/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 5 hook(s) need dependency review; fix each and remove this directive */
import { getBackendUrl, getImageUrl } from "@Utils";
import { sanitizeHtml } from "@Utils/sanitize";
import { useCallback, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "@Reducers/index";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  getIssueViewpointData,
  getIssuesData,
  isIssueRequiredPermission,
} from "@Features/issues/state/bcfData.actions";
import { setEditIssuesData } from "@Features/issues/state/editIssues";
import CommentsList from "@Components/Comments/CommentsList";
import { cubeView } from "@Services/cubeView";
import { convertDate } from "@Services/luxonHelper";
import { CubeGlobal, HoopsViewerContext, useViewerCallbacks } from "@Features/viewer-core";
import { QueryBuilder } from "@Services/queryBuilder";
import {
  setAnnotations,
  setCurrentPagePDF,
  setModalStatus,
  setBottomToolbarRefresh,
} from "../../../../reducers/Viewer";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import { getUrlForImage } from "@CubeComponents/constants/globalFunctions";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import { deleteIssueData } from "@Features/issues/state/deletingIssues.actions";
import { deleteIssuesPinData } from "@Features/issues/state/issuesPinDataSlice";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import CustomFields from "@Components/IssuesCustomFields/CustomFields";
import { folderPermissions, getViewerUrl } from "@Helpers";
import { BiLinkExternal } from "react-icons/bi";
import { PDF_ENGINE, PLATFORM } from "@Services/constants";
import useDebounce from "@Components/Search/useDebounce";
import { getReviewsListForEntity } from "@Features/reviews/helpers";
import axiosWrapper from "@AxiosWrapper";
import type { RecentAccessResponse } from "@ServerTypes/recentAccess/recentAccess.schema";
import type { IssueItem, GetIssuesDataRequest } from "@ServerTypes/issues/issues.schema";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { recentAccessConstant } from "@Components/RecentAccess/recentAccessConstant";
import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";
import { useTranslation } from "@i18n";
import IconComponent from "@CubeComponents/icons";
import { Box } from "@mui/material";
import { getIsViewerDependencyLoaded } from "@Services/utils/helpers";
import { SimpleEditor } from "@CubeComponents/RichTextEditor/components/tiptap-templates/simple/ControlledSimpleEditor";
import Text from "@CubeComponents/html/Text/Text";
import { dispatchModelLoadingToast } from "@Actions/Viewer";
import Shimmer from "@Components/Queries/Shimmer";
import IssueErrorIllustration from "./IssueErrorIllustration";
import IssueStatusPopover from "../IssueStatusPopover/IssueStatusPopover";

const { RESOURCE_DELETE, RESOURCE_MODIFY, RESOURCE_COMMENT } = folderPermissions();

type IssueRequestStateType = {
  loading: boolean;
  error: null | number;
};

// The issue being viewed is fully loaded from the server; these fields are always
// present on a stored issue (the server `IssueItem` types them optional for list/create shapes).
type LoadedIssue = IssueItem & {
  id: string;
  title: string;
  pinData: string;
  pdfCurrentPage: number;
  zoomLevel: number;
  xScrollPosition: number;
  yScrollPosition: number;
};

type AttachedFile = {
  name: string | undefined;
  fileVersionId: string | undefined;
  resourceType: string | undefined;
};

type FormTemplateSection = NonNullable<IssueItem["formTemplate"]>[number];
type FormTemplateQuestion = NonNullable<FormTemplateSection["questionFields"]>[number];
type FormTemplateAnswer = FormTemplateQuestion["answer"];

interface ViewIssueProps {
  engineType?: string;
  isStandalone?: boolean;
  spaceId?: string;
  fileVersionId?: string;
  fileType?: string;
  selectedIssues?: Record<string, boolean>;
  issue?: IssueItem;
  refreshData?: () => void;
  currentViewIssueId: string;
  handleSelectedIssues: (issueId: string, deleteAll?: boolean) => void;
  closeAddIssueModal?: () => void;
}

function ViewIssue(props: ViewIssueProps) {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const spaceId = params.get("spaceId");

  const issues = useSelector((state: RootState) => state.issues);
  const HoopsModalStatus = useSelector((state: RootState) => state.HoopsModalStatus);

  const { viewerImpl, isModelStructureReady, isSettingsApplied } = useContext(HoopsViewerContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [pdfViewer] = useState(props.engineType === PDF_ENGINE);
  const [isTheIssuePartOfReview, setIsTheIssuePartOfReview] = useState<boolean | null>(null);

  const dispatch = useDispatch<AppDispatch>();
  const query = new QueryBuilder();
  props.isStandalone
    ? query.addParams("entityType", "ISSUES").addParams("spaceId", props.spaceId)
    : query.addParams("entityType", "ISSUES").addParams("spaceId", spaceId);

  const { currentViewIssueId, handleSelectedIssues } = props;
  const [addComment, setAddComment] = useState<boolean[]>([]);
  const [openDeleteModalPopUp, setOpenDeleteModalPopUp] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [accordionState, setAccordionState] = useState({ customFields: false });
  const [imageDataForStandaloneIssue, setImageDataForStandaloneIssue] = useState(
    getUrlForImage({ id: currentViewIssueId }, "IssueImage"),
  );
  const [issueRequestState, setIssueRequestState] = useState<IssueRequestStateType>({
    loading: true,
    error: null,
  });

  let disablingEditAndDeleteInIssuesPartOfFileInForms = true;
  if (props.isStandalone) {
    if (issues[currentViewIssueId]?.fileVersionId) {
      disablingEditAndDeleteInIssuesPartOfFileInForms = false;
    }
  }

  const fetchReviewsListForEntity = useCallback(async () => {
    const resource = {
      issueId: currentViewIssueId,
      resourceType: "RESOURCE_TYPE_ISSUE",
    };
    const includeDefaultReviews = false;
    // `spaceId` is always supplied by the callers that surface reviews (pallet / review modal).
    const params = { resource, spaceId: props.spaceId as string, includeDefaultReviews };
    const response = await getReviewsListForEntity({ value: { params } });

    if (response.data.result?.length) {
      setIsTheIssuePartOfReview(true);
    } else {
      setIsTheIssuePartOfReview(false);
    }
  }, [currentViewIssueId, props.spaceId]);

  useEffect(() => {
    fetchReviewsListForEntity();
  }, [fetchReviewsListForEntity]);

  const dropDownButtonItems = props.isStandalone
    ? [
        {
          text: "Edit",
          id: "edit",
          disabled: !isIssueRequiredPermission(issues[currentViewIssueId], RESOURCE_MODIFY.id),
        },
      ]
    : [
        {
          text: "Edit",
          id: "edit",
          disabled:
            !getIsViewerDependencyLoaded({
              viewerImpl,
              hwv: CubeGlobal.hwv,
              isModelStructureReady,
              isSettingsApplied,
            }) || !isIssueRequiredPermission(issues[currentViewIssueId], RESOURCE_MODIFY.id),
        },
        {
          text: "Delete",
          id: "delete",
          disabled: !isIssueRequiredPermission(issues[currentViewIssueId], RESOURCE_DELETE.id),
        },
      ];

  const handleOpenLink = (
    dependency: { fileVersionId?: string; spaceId?: string; projectId?: string },
    newTab: boolean,
  ) => {
    const location = getViewerUrl({
      fileVersionId: dependency.fileVersionId,
      spaceId: dependency.spaceId,
      projectId: dependency.projectId,
    });
    if (location) {
      newTab ? window.open(location, "_blank") : navigate(location);
    }
  };

  useEffect(() => {
    handleSelectedIssues("removeAll", true);
  }, []);

  const setRefreshBottomToolbarDebounce = useDebounce(() => {
    dispatch(setBottomToolbarRefresh(true));
  }, 500);

  const viewerCallbacks = {
    frameDrawn() {
      setRefreshBottomToolbarDebounce();
    },
  };

  const currentIssue = issues[currentViewIssueId];
  useEffect(() => {
    if (currentIssue?.id) {
      const searchParams: Record<string, string> = {};
      const urlSearchParams = new URLSearchParams(location.search);
      for (const [key, value] of urlSearchParams) {
        searchParams[key] = value;
      }
      axiosWrapper.post<RecentAccessResponse>(`${getBackendUrl()}/recentAccess/recentAccess`, {
        module: recentAccessConstant.issue,
        metaData: {
          _id: currentViewIssueId,
          name: currentIssue.title,
          fileVersionId: currentIssue.fileVersionId,
          projectId: currentIssue.projectUniqueId,
          ...searchParams,
        },
        projectId: currentIssue.projectUniqueId,
        platform: PLATFORM.web,
        ...searchParams,
      });
    }
  }, [
    currentIssue?.id,
    currentIssue?.fileVersionId,
    currentIssue?.projectUniqueId,
    currentIssue?.title,
    currentViewIssueId,
    location.search,
  ]);

  useViewerCallbacks({ viewerCallbacks });

  useEffect(() => {
    if (currentIssue && !currentIssue.isOldPdfIssue && currentIssue.id && currentIssue.viewpoint === undefined) {
      dispatch(getIssueViewpointData({ id: currentIssue.id }));
    }
  }, [currentIssue?.id]);

  useEffect(() => {
    const issueAttachedFiles = currentIssue?.attachedFiles;
    if (issueAttachedFiles?.length) {
      const files: AttachedFile[] = [];
      issueAttachedFiles.map((file) => {
        files.push({
          name: file.originalName,
          fileVersionId: file.fileVersionId,
          resourceType: file.resourceType,
        });
      });
      setAttachedFiles(files);
    }
  }, [issues]);

  const handleSelectedOption = (args: { item: { id?: string } }, issueId: string) => {
    if (args.item.id === "edit") {
      handleEditIssueClick(issueId);
    } else {
      setOpenDeleteModalPopUp(true);
    }
  };

  const handleSnapshotClick = async (issueId: string, showToast = false) => {
    // A snapshot is only taken for a fully-loaded issue (PDF positioning fields are present).
    const snapshotIssue = issues[issueId] as LoadedIssue | undefined;
    if (snapshotIssue?.viewpoint) {
      if (!pdfViewer) {
        const isViewerReady = getIsViewerDependencyLoaded({
          viewerImpl,
          hwv: CubeGlobal.hwv,
          isSettingsApplied,
          isModelStructureReady,
        });

        if (!isViewerReady) {
          showToast && dispatch(dispatchModelLoadingToast());
          return;
        }

        await cubeView.flushView();
        await cubeView.fromJson(JSON.parse(snapshotIssue.viewpoint), props.fileVersionId);

        const markupsToRemove = viewerImpl?.hwv.measureManager
          .getAllMeasurements()
          .filter((record: any) => record._name == "CustomPinMarkup");
        for (const markup of markupsToRemove) {
          viewerImpl?.hwv.measureManager.removeMeasurement(markup);
        }

        if (currentIssue?.pinData) {
          const pinData = JSON.parse(currentIssue.pinData);
          if (pinData._uniqueId) {
            await viewerImpl?.loadMarkups([pinData]);
          }
        }
      } else {
        // TODO: pradeep Quick patch as delete anotations are crashing again
        try {
          if (snapshotIssue.viewpoint) {
            dispatch(
              setAnnotations({ data: JSON.parse(snapshotIssue.viewpoint), annotationType: "markupAnnotations" }),
            );
            dispatch(
              setCurrentPagePDF({
                pageNumber: snapshotIssue.pdfCurrentPage,
                reason: "viewIssue",
                zoomLevel: snapshotIssue.zoomLevel,
                xScrollPosition: snapshotIssue.xScrollPosition,
                yScrollPosition: snapshotIssue.yScrollPosition,
              }),
            );
          }
        } catch (e) {
          console.log(e);
          Promise.reject(e);
        }
      }
    }
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const issueId = searchParams.get("issueId");

    if (viewerImpl && issueId && viewerImpl?.hwv) {
      searchParams.delete("issueId");
      navigate(`${location.pathname}?${searchParams.toString()}`);
    }
  }, []);

  const handleAddComment = (index: number) => {
    const tempAddComment = [...addComment];
    tempAddComment[index] = !tempAddComment[index];
    setAddComment(tempAddComment);
  };

  const handleEditIssueClick = (issueId: string) => {
    const issueToEdit = issues[issueId];
    if (issueToEdit) {
      dispatch(setEditIssuesData(issueToEdit));
    }
    dispatch(
      setModalStatus({
        ...HoopsModalStatus,
        Issues: {
          ...HoopsModalStatus.Issues,
          currentStage: "edit_issue",
          backStage: "view_issue",
        },
      }),
    );

    const activeView = hwv?.markupManager?.getActiveMarkupView?.(hwv.view);
    (viewerImpl as any)?.setMakrupViewEditMode?.(activeView, true);
  };

  const getIssues = useCallback(async () => {
    const params: GetIssuesDataRequest = {
      issueId: currentViewIssueId,
    };
    const issueVersionId = currentIssue?.issueVersionId;
    if (issueVersionId) {
      params.issueVersionId = issueVersionId;
    }
    dispatch(getIssuesData(params, false, setIssueRequestState));
  }, [currentViewIssueId, currentIssue?.issueVersionId, dispatch]);

  useEffect(() => {
    getIssues();
  }, [getIssues]);

  const handleLoad = async () => {
    if (
      (getIsViewerDependencyLoaded({ viewerImpl, isModelStructureReady, isSettingsApplied, hwv: CubeGlobal.hwv }) ||
        pdfViewer) &&
      issues[currentViewIssueId]?.viewpoint !== undefined
    ) {
      const currentIssue = issues[currentViewIssueId];
      if (currentIssue) {
        await handleSnapshotClick(currentViewIssueId);
      }
    }
  };

  const currentIssueViewpoint = issues[currentViewIssueId]?.viewpoint;
  useEffect(() => {
    handleLoad();
  }, [isModelStructureReady, isSettingsApplied, currentIssueViewpoint]);

  const handleIssueDelete = async () => {
    if (
      !pdfViewer &&
      !props.isStandalone &&
      getIsViewerDependencyLoaded({ viewerImpl, isModelStructureReady, isSettingsApplied, hwv: CubeGlobal.hwv })
    ) {
      const measurementIds = [JSON.parse((currentIssue as LoadedIssue).pinData)._uniqueId];
      const allMeasurementsInViewer = viewerImpl?.hwv.measureManager.getAllMeasurements();
      const markupsToRemove = allMeasurementsInViewer.filter((record: any) =>
        measurementIds.includes(record._uniqueId),
      );
      markupsToRemove.forEach((markup: any) => viewerImpl?.hwv.measureManager.removeMeasurement(markup));
      dispatch(deleteIssuesPinData([currentViewIssueId]));
    }

    dispatch(deleteIssueData([currentViewIssueId]));
    dispatch(
      setModalStatus({
        ...HoopsModalStatus,
        Issues: {
          ...HoopsModalStatus.Issues,
          currentStage: HoopsModalStatus.Issues?.defaultStage,
          backStage: HoopsModalStatus.Issues?.defaultStage,
        },
      }),
    );

    setOpenDeleteModalPopUp(false);
    if (props.closeAddIssueModal) {
      props?.closeAddIssueModal();
    }
  };

  const closeDeleteModal = () => {
    setOpenDeleteModalPopUp(false);
  };

  const getUserAvatar = (id: string | undefined) => <UserNameTagComponent id={id} />;

  const deletePopup = () => (
    <div>
      <ModalStateDialogComponent
        footerContent={
          <div className="flex justify-between w-full">
            <ButtonComponent onClick={closeDeleteModal} buttonType="borderButton">
              <Text>{t("Cancel")}</Text>
            </ButtonComponent>
            <ButtonComponent onClick={handleIssueDelete} buttonType="gradientButton">
              <Text>{t("Delete")}</Text>
            </ButtonComponent>
          </div>
        }
        onCancel={closeDeleteModal}
        headerTitle={
          <>
            <Text>{t("Delete all selected?")}</Text>
          </>
        }
        isVisible={openDeleteModalPopUp}
        modelContent={
          <Text sx={{ whiteSpace: "normal" }}>{t("Are you sure you want to delete the selected items?")}</Text>
        }
        width="23.125rem"
      />
    </div>
  );

  const customFieldsHeader = () => (
    <div className="font-semibold text-lg">
      <Text>{t("Custom Fields")}</Text>
    </div>
  );

  const getAnswerValue = (answer: FormTemplateAnswer, fieldType: FormTemplateQuestion["fieldType"]) => {
    if (!answer) return "-";

    const value = answer.value;

    if (fieldType === "date-time") {
      return convertDate(Number(value), "NormalDate") || "-";
    }
    if (Array.isArray(value)) {
      return value
        .map((item: unknown) => {
          if (item && typeof item === "object") {
            return (item as { name?: string }).name;
          }
          return item;
        })
        .join(", ");
    }
    return (value as string | number | undefined) || "-";
  };

  const customFieldsContent = (issuesData: LoadedIssue) => (
    <div className="flex flex-col gap-8">
      {issuesData.formTemplate?.map((section: FormTemplateSection, index: number) => (
        <div key={index} className="flex flex-col gap-6">
          <div key={section._id} className="font-semibold text-base">
            {section.sectionHeading}
          </div>
          <div className="flex flex-col gap-4">
            {section.questionFields?.map((field: FormTemplateQuestion, index: number) => (
              <div key={index} className="flex flex-col gap-2">
                <div className="flex flex-1 justify-self-start font-semibold text-sm">{field.questionValue}</div>
                <div className="flex-1 justify-self-start text-xs">{getAnswerValue(field.answer, field.fieldType)}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  const handleImageError = () => {
    setImageDataForStandaloneIssue(`${getImageUrl()}/Issues_default_image.svg`);
  };

  const issueHtml = () => {
    // Only rendered when `issues[currentViewIssueId]` is present (guarded at the call site),
    // and it is the fully-loaded issue being viewed.
    const issuesData = issues[currentViewIssueId] as LoadedIssue;

    return (
      issuesData.id && (
        <div
          className="rounded shadow-[0_0_0.25rem_0_var(--black-15)] flex flex-col gap-4 pt-5 overflow-auto"
          key={`issue_${issuesData.issueCounter}`}
        >
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-3 text-xs ps-2 text-[#192048]">
              <IconComponent name="bx_hash" width="1rem" height="1rem" />
              <span>{issuesData.issueCounter}</span>
              {!issuesData.isFormTemplateValid && (
                <IconComponent
                  name="ErrorIcon"
                  toolTipContent={t("Mandatory custom fields not filled.")}
                  position="bottom"
                  width="1rem"
                  height="1rem"
                />
              )}
            </div>
            <div className="flex items-center gap-3 text-xs ps-2 text-[#192048]">
              <IconComponent name="bx_time-five" width="0.75rem" height="0.75rem" />
              <span className="text-[#666a84]">
                <ToolTipComponent
                  content={convertDate(Number(issuesData.createdDate), "dateForTooltip")}
                  position="bottom"
                >
                  {convertDate(Number(issuesData.createdDate), "dateForText")}
                </ToolTipComponent>
              </span>
              {!isTheIssuePartOfReview &&
                isTheIssuePartOfReview !== null &&
                disablingEditAndDeleteInIssuesPartOfFileInForms && (
                  <SelectDropDown
                    select={(args) => handleSelectedOption(args, issuesData.id)}
                    items={dropDownButtonItems}
                    cssClass="e-caret-hide p-0 bg-white border-0 flex"
                  >
                    <IconComponent
                      name="bi_three-dots_verti"
                      width="1rem"
                      height="1rem"
                      className="w-[0.8125rem] h-[0.8125rem]"
                    />
                  </SelectDropDown>
                )}
            </div>
          </div>
          <div className="text-sm font-semibold px-2.5 text-[#192048] flex items-center justify-between">
            <span>{issuesData.title}</span>
            {deletePopup()}
          </div>
          <div className="flex flex-col w-[96%] gap-3 px-1.5">
            <div className="flex flex-col gap-3">
              {props.isStandalone ? (
                <div className="w-[31.25rem] h-[12.5rem]">
                  <img
                    src={imageDataForStandaloneIssue}
                    className="w-full h-full object-cover"
                    onError={handleImageError}
                    alt=""
                  />
                </div>
              ) : (
                <Box className="w-full h-[14.125rem]" onClick={() => handleSnapshotClick(issuesData.id, true)}>
                  <img
                    className="w-full h-full flex-grow-0 rounded-md shadow-[0_0_0.25rem_0_rgb(0_0_0_/_15%)] m-0 object-contain"
                    src={getUrlForImage({ id: issuesData.id }, "IssueImage")}
                    alt=""
                  />
                </Box>
              )}
            </div>
            <div className="max-h-20 overflow-auto">
              <SimpleEditor
                showToolbar={false}
                editable={false}
                value={sanitizeHtml(issuesData.description)}
                showCharCount={false}
              />
            </div>
            <div className="flex flex-col gap-3 mt-1">
              <div className="flex flex-col gap-4 text-sm">
                <div className="flex gap-6 items-center">
                  <Text className="w-20 font-semibold">{t("Created by")}</Text>
                  {issuesData.createdByName
                    ? getUserAvatar(issuesData.createdBy as string | undefined)
                    : getUserAvatar((issuesData.createdBy as { _id?: string } | undefined)?._id)}
                </div>
                <div className="flex gap-6 items-center">
                  <Text className="w-20 font-semibold">{t("Status")}</Text>
                  <IssueStatusPopover
                    issue={{
                      id: issuesData.id,
                      spaceId: issuesData.spaceId ?? "",
                      fileVersionId: issuesData.fileVersionId,
                      statusInReview: issuesData.statusInReview,
                      reviews: issuesData.reviews,
                      canEditDefaultStatus: isIssueRequiredPermission(issuesData, RESOURCE_COMMENT.id),
                    }}
                    canEdit
                  />
                </div>
                <div className="flex gap-6">
                  <Text className="w-20 font-semibold">{t("Assigned to")}</Text>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center justify-start gap-2">
                      {issuesData.assignedTo?.length
                        ? issuesData.assignedTo.map((item) => <UserNameTagComponent key={item._id} id={item._id} />)
                        : "-"}
                    </div>
                  </div>
                </div>
              </div>

              <CustomFields dueDate={issuesData.dueDate} customMetaData={issuesData.customFields} />

              {attachedFiles.length ? (
                <>
                  <div className="mt-2">
                    <Text className="font-semibold">{t("Attachments")}</Text>
                  </div>
                  <div className="flex flex-col gap-2.5 pb-2.5">
                    {attachedFiles.map((file, index: number) => (
                      <div key={index} className="flex gap-2.5 items-center">
                        <div>{file.name}</div>
                        <BiLinkExternal
                          onClick={() =>
                            handleOpenLink(
                              {
                                fileVersionId: file.fileVersionId,
                                spaceId: issuesData.spaceId,
                                projectId: issuesData.projectUniqueId,
                              },
                              true,
                            )
                          }
                        />
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                ""
              )}
            </div>
          </div>
          <div>
            {issuesData.formTemplate && (
              <Accordion
                disableGutters
                expanded={accordionState.customFields}
                onChange={() =>
                  setAccordionState((prev) => ({
                    ...prev,
                    customFields: !prev.customFields,
                  }))
                }
                className="shadow-none border-b-0.5 border-[#ecf3fe] [&>div>div]:bg-white"
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} className="px-4 py-2.5 flex items-center gap-2">
                  {customFieldsHeader()}
                </AccordionSummary>
                <AccordionDetails>{customFieldsContent(issuesData)}</AccordionDetails>
              </Accordion>
            )}
          </div>
          <div className="mb-2">
            <CommentsList
              issueId={issuesData.id}
              elements={!pdfViewer && issuesData?.viewpoint ? JSON.parse(issuesData?.viewpoint).selection || [] : []}
              fileVersionId={props.fileVersionId}
              handleAddComment={handleAddComment}
              issueIndex={issuesData.issueCounter}
              showAddComment
              pdfViewer={pdfViewer}
              issueIsStandalone={props.isStandalone}
              offialCommentId={issuesData.officialCommentId}
            />
          </div>
        </div>
      )
    );
  };

  return (
    <div className="flex h-full overflow-auto flex-col ps-[1.125rem] pe-3">
      {issueRequestState.loading ? (
        <Shimmer />
      ) : issueRequestState.error ? (
        <IssueErrorIllustration errorCode={issueRequestState.error} />
      ) : (
        issues[currentViewIssueId] && issueHtml()
      )}
    </div>
  );
}

export default ViewIssue;
